// dashboard/frontend/src/components/Pricing/PricingDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
    Link,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    CircularProgress,
    TextField,
    Alert,
    Fade
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { squareConfig } from '../../utils/squareConfig';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const PricingCard = ({ 
    title, 
    subtitle,
    setupFee, 
    monthlyFee, 
    features, 
    onSelect,
    highlighted = false,
    customCTA = null 
}) => (
    <Card 
        elevation={highlighted ? 8 : 3} 
        sx={{ 
            height: '100%',
            border: highlighted ? '2px solid primary.main' : 'none',
            position: 'relative',
            transition: '0.3s',
            transform: highlighted ? 'translateY(-8px)' : 'none', // Always elevated if highlighted
            boxShadow: highlighted ? 8 : 3, // Always stronger shadow if highlighted
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
                transform: highlighted ? 'translateY(-8px)' : 'none', // Remove hover effect
                boxShadow: highlighted ? 8 : 3 // Keep shadow consistent
            },
            }}
    >
        <CardContent sx={{ 
            pt: highlighted ? 4 : 2,
            pb: highlighted ? 4 : 2,
            textAlign: 'center',
            display: 'flex', // Add flex display
            flexDirection: 'column', // Stack content vertically
            height: '100%', // Take full height
            justifyContent: 'space-between', // Distribute space between elements
        }}>
            <Box>  {/* Header content group */}
                <Typography variant="h5" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {subtitle}
                </Typography>
                <Typography variant="h4" gutterBottom>
                    {typeof monthlyFee === 'number' ? `$${monthlyFee}/mo` : monthlyFee}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {typeof setupFee === 'number' ? `$${setupFee} setup fee` : setupFee}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>  
                {/* Features list - centered in the middle */}
                <List sx={{ 
                    textAlign: 'left',
                    mt: 2,
                    mb: 2
                }}>
                    {features.map((feature, index) => (
                        <ListItem key={index} dense>
                            <ListItemIcon>
                                <CheckIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={feature} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Box>  {/* Footer content group */}
                <Button
                    variant={highlighted ? "contained" : "outlined"}
                    color="primary"
                    fullWidth
                    onClick={onSelect}
                >
                    {customCTA || 'Select Plan'}
                </Button>
            </Box>
        </CardContent>
    </Card>
);

const PricingDialog = ({ open, onClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [codeError, setCodeError] = useState(null);
    const [paymentError, setPaymentError] = useState(null);
    const [codeValidated, setCodeValidated] = useState(false);
    const [adjustedPricing, setAdjustedPricing] = useState(null);
    const [formError, setFormError] = useState(null);
    const [customerInfoErrors, setCustomerInfoErrors] = useState({});
    const [registrationCode, setRegistrationCode] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFormLoading, setIsFormLoading] = useState(true);
    const [formMounted, setFormMounted] = useState(false);
    const [step, setStep] = useState('plan'); // 'plan', 'info', or 'payment'
    const [customerInfo, setCustomerInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
    });

    useEffect(() => {
        // Only initialize Square if we're in payment step AND not using a bypass/zero payment code
        if (selectedPlan && open && step === 'payment' && !adjustedPricing?.bypass) {
            setIsFormLoading(true);
            setError(null);
            initializeSquare().catch(err => {
                setPaymentError(err.message || 'Failed to initialize payment form');
            });
        }
        return () => {
            if (card) {
                card.destroy();
            }
            setFormMounted(false);
        };
    }, [selectedPlan, open, step, adjustedPricing?.bypass]); // Add dependencies

    useEffect(() => {
        if (isFormLoading) {
            setPaymentError(null);
        }
    }, [isFormLoading]);

    const initializeSquare = async () => {
        setIsFormLoading(true);
        setError(null);
        
        if (!window.Square) {
            setError('Payment system failed to load');
            setIsFormLoading(false);
            return;
        }

        try {
            const payments = window.Square.payments(
                squareConfig.applicationId, 
                squareConfig.locationId
            );
            
            const card = await payments.card();
            await card.attach('#card-container');
            setCard(card);
            setFormMounted(true);
        } catch (e) {
            console.error('Square initialization error:', e);
            setError('Failed to load payment form');
        } finally {
            setIsFormLoading(false);
        }
    };

    const handleCustomerInfoChange = (e) => {
        setCustomerInfo({
            ...customerInfo,
            [e.target.name]: e.target.value
        });
    };

    const validateCustomerInfo = () => {
        const errors = {};
        if (!customerInfo.firstName) errors.firstName = 'First name is required';
        if (!customerInfo.lastName) errors.lastName = 'Last name is required';
        if (!customerInfo.email) errors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) errors.email = 'Email is invalid';
        
        setCustomerInfoErrors(errors);
        return Object.keys(errors).length === 0;
    };
    
    const handleCodeSubmit = async () => {
        try {
            setCodeError(null);
            const response = await api.post('/auth/verify-code/', {
                code: registrationCode
            });

            if (response.data.valid) {
                setCodeValidated(true);
                
                // Handle different code types
                if (response.data.type === 'bypass_payment') {
                    // Handle complete bypass
                    localStorage.setItem('pendingRegistration', JSON.stringify({
                        customerInfo,
                        plan: response.data.subscription_level,
                        registrationCode: registrationCode
                    }));
                    setAdjustedPricing({
                        setupFee: 0,
                        monthlyFee: 0,
                        bypass: true
                    });
                } else if (response.data.type === 'zero_payment' || response.data.type === 'discount') {
                    // Handle zero payment or discounted pricing
                    const adjustedPrices = calculateAdjustedPrices(selectedPlan, response.data.discount);
                    setAdjustedPricing(adjustedPrices);
                }
            }
        } catch (error) {
            setCodeError('Invalid registration code');
            setCodeValidated(false);
            setAdjustedPricing(null);
        }
    };

    // Helper function to calculate adjusted prices
    const calculateAdjustedPrices = (plan, discount) => {
        if (!plan) return null;
        return {
            setupFee: discount.setupFeeDiscount ? 
                     Math.round(plan.setupFee * (1 - discount.setupFeeDiscount)) : 
                     plan.setupFee,
            monthlyFee: discount.monthlyFeeDiscount ? 
                       Math.round(plan.monthlyFee * (1 - discount.monthlyFeeDiscount)) : 
                       plan.monthlyFee,
            bypass: false
        };
    };

    const handleSelect = (plan) => {
        setSelectedPlan(plan);
        setStep('info'); // Go to customer info instead of directly to payment
    };

    const handleCustomerInfoSubmit = () => {
        if (validateCustomerInfo()) {
            if (adjustedPricing?.bypass) {
                // If using bypass code, go straight to registration
                localStorage.setItem('pendingRegistration', JSON.stringify({
                    customerInfo,
                    plan: selectedPlan.title,
                    registrationCode: registrationCode
                }));
                onClose();
                navigate('/auth/register');
            } else {
                // Otherwise continue to payment
                setStep('payment');
            }
        }
    };

    const handlePayment = async () => {
        // For zero-payment cases
        if (adjustedPricing?.setupFee === 0) {
            try {
                const response = await fetch('/api/payments/create-payment/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        plan: selectedPlan,
                        customerInfo: customerInfo,
                        registrationCode: registrationCode,
                        zeroPayment: true
                    })
                });
    
                const data = await response.json();
                if (data.success) {
                    localStorage.setItem('pendingRegistration', JSON.stringify({
                        paymentId: data.payment_id,
                        plan: selectedPlan.title,
                        customerInfo: customerInfo,
                        registrationCode: registrationCode
                    }));
                    window.location.href = data.redirect_url;
                } else {
                    setError(data.error || 'Failed to process request');
                }
                return;
            } catch (e) {
                setError(e.message);
                return;
            }
        }
    
        // Regular payment processing
        if (!card) {
            setPaymentError('Payment form not initialized');
            setError('Payment form not initialized');
            return;
        }
        setLoading(true);
        setError(null);
    
        try {
            setPaymentError(null);
            const result = await card.tokenize();
            if (result.status === 'OK') {
                // Calculate final payment amount
                const finalPlan = adjustedPricing ? {
                    ...selectedPlan,
                    setupFee: adjustedPricing.setupFee,
                    monthlyFee: adjustedPricing.monthlyFee
                } : selectedPlan;
    
                const response = await fetch('/api/payments/create-payment/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sourceId: result.token,
                        plan: finalPlan,
                        customerInfo: customerInfo,
                        registrationCode: codeValidated ? registrationCode : null // Include validated code
                    })
                });
    
                const data = await response.json();
                if (data.success) {
                    localStorage.setItem('pendingRegistration', JSON.stringify({
                        paymentId: data.payment_id,
                        plan: finalPlan.title,
                        customerInfo: customerInfo,
                        registrationCode: codeValidated ? registrationCode : null,
                        adjustedPricing: adjustedPricing // Store pricing info for reference
                    }));
                    window.location.href = data.redirect_url;
                } else {
                    setError(data.error || 'Payment failed');
                }
            } else {
                setError(result.errors[0].message);
            }
        } catch (e) {
            setPaymentError(e.message || 'Payment processing failed');
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (step === 'payment') {
            setStep('info');
            if (card) {
                card.destroy();
                setCard(null);
            }
            setFormMounted(false);
        } else if (step === 'info') {
            setStep('plan');
            setSelectedPlan(null);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ px: '10px', py: '5px' }}>
                {step === 'plan' && 'Choose Your Plan'}
                {step === 'payment' && `Complete Your ${selectedPlan.title} Plan Purchase`}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {step === 'plan' && (
                    <Grid container spacing={3} sx={{ px: 2 }}>
                        <Grid item xs={12} md={4}>
                            <PricingCard
                                title="BYOE"
                                subtitle="Bring Your Own Email"
                                setupFee={0}
                                monthlyFee={199}
                                features={[
                                    'Connect 3+ Gmail accounts',
                                    'AI message generation',
                                    'Advanced personalization',
                                    'Standard support',
                                    'Up to 1,000 monthly emails per connected account'
                                ]}
                                onSelect={() => handleSelect({
                                    title: 'BYOE',
                                    setupFee: 0,
                                    monthlyFee: 199
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <PricingCard
                                title="Professional"
                                subtitle="Full-Service Solution"
                                setupFee={999}
                                monthlyFee={499}
                                features={[
                                    '5 Gmail accounts included',
                                    'Domain registration & setup',
                                    'Full infrastructure config',
                                    'Advanced personalization',
                                    'Personal support',
                                    'Up to 5,000 emails/month'
                                ]}
                                highlighted={true}
                                onSelect={() => handleSelect({
                                    title: 'Professional',
                                    setupFee: 999,
                                    monthlyFee: 499
                                })}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <PricingCard
                                title="Enterprise"
                                subtitle="Agency Solution"
                                setupFee="Custom"
                                monthlyFee="Custom"
                                features={[
                                    'Custom account volume',
                                    'White-label options',
                                    'Agency dashboard',
                                    'Dedicated support',
                                    'Custom integrations',
                                    'Unlimited emails'
                                ]}
                                customCTA="Contact Sales"
                                onSelect={() => window.location.href = '/contact'}
                            />
                        </Grid>
                    </Grid>
                )}
                
                {step === 'info' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Your Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={customerInfo.firstName}
                                    onChange={handleCustomerInfoChange}
                                    error={!!customerInfoErrors.firstName}
                                    helperText={customerInfoErrors.firstName}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={customerInfo.lastName}
                                    onChange={handleCustomerInfoChange}
                                    error={!!customerInfoErrors.lastName}
                                    helperText={customerInfoErrors.lastName}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={customerInfo.email}
                                    onChange={handleCustomerInfoChange}
                                    error={!!customerInfoErrors.email}
                                    helperText={customerInfoErrors.email}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Company Name"
                                    name="companyName"
                                    value={customerInfo.companyName}
                                    onChange={handleCustomerInfoChange}
                                />
                            </Grid>
                        </Grid>
                        
                        {/* Add registration code section here */}
                        <Box sx={{ 
                            mt: 2, 
                            mb: 2,
                            display: 'flex',
                            gap: 2,
                        }}>
                            <TextField
                                fullWidth
                                label="Registration Code (if you have one)"
                                value={registrationCode}
                                onChange={(e) => {
                                    setRegistrationCode(e.target.value);
                                    setCodeError(null);
                                    setCodeValidated(false);
                                }}
                                error={!!codeError}
                                helperText={codeError || (codeValidated ? 'Code validated!' : '')}
                                InputProps={{
                                    sx: codeValidated ? {
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.success.main,
                                        },
                                    } : {}
                                }}
                            />
                            {registrationCode && !codeValidated && (
                                <Button
                                    variant="outlined"
                                    onClick={handleCodeSubmit}
                                    sx={{ 
                                        height: '56px',
                                        width: '125px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Apply Code
                                </Button>
                            )}
                            {codeValidated && adjustedPricing && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography color="success.main">
                                        {adjustedPricing.bypass ? 
                                            'Code applied - No payment required!' :
                                            `Adjusted pricing: Setup $${adjustedPricing.setupFee}, Monthly $${adjustedPricing.monthlyFee}`
                                        }
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleCustomerInfoSubmit}
                                sx={{ flex: 1 }}
                            >
                                {adjustedPricing?.bypass ? 
                                    'Continue to Create Account' : 
                                    'Continue to Payment'
                                }
                            </Button>
                        </Box>
                    </Box>
                )}
                {step === 'payment' && (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {selectedPlan.title} Plan
                        </Typography>

                        {/* Pricing section */}
                        <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            {/* Original price */}
                            <Grid item xs={12} md={codeValidated && adjustedPricing ? 6 : 12}>
                                <Typography variant="body1" gutterBottom sx={codeValidated && adjustedPricing ? {
                                    textDecoration: 'line-through',
                                    color: 'text.secondary'
                                } : {}}>
                                    Setup Fee: ${selectedPlan.setupFee}
                                    <br />
                                    Monthly Fee: ${selectedPlan.monthlyFee}
                                </Typography>
                            </Grid>

                            {/* Discounted price - only shows if there's a valid code */}
                            {codeValidated && adjustedPricing && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body1" gutterBottom color="success.main" sx={{ fontWeight: 'bold' }}>
                                        {adjustedPricing.setupFee === 0 ? (
                                            'Save payment method for future billing'
                                        ) : (
                                            <>
                                                Discounted Price:
                                                <br />
                                                Setup Fee: ${adjustedPricing.setupFee}
                                                <br />
                                                Monthly Fee: ${adjustedPricing.monthlyFee}
                                            </>
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>

                        {/* Payment form section - full width */}
                        {(!adjustedPricing || adjustedPricing.setupFee > 0) && (
                            <Box sx={{ 
                                width: '100%',
                                position: 'relative', 
                                minHeight: '85px'
                            }}>
                                <Fade in={isFormLoading}>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'background.paper',
                                        zIndex: 1,
                                    }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                            Loading payment form...
                                        </Typography>
                                    </Box>
                                </Fade>
                                
                                <Fade in={formMounted}>
                                    <Box 
                                        id="card-container" 
                                        sx={{ 
                                            border: '1px solid #ccc',
                                            borderRadius: 1,
                                            p: 2,
                                            minHeight: '85px',
                                            visibility: formMounted ? 'visible' : 'hidden'
                                        }}
                                    />
                                </Fade>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setStep('info')}
                                disabled={loading}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePayment}
                                disabled={loading || ((!formMounted && !adjustedPricing?.setupFee === 0))}
                                sx={{ flex: 1 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} />
                                ) : adjustedPricing?.setupFee === 0 ? (
                                    'Save Billing Info & Continue'
                                ) : (
                                    `Pay $${adjustedPricing?.setupFee || selectedPlan.setupFee}`
                                )}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PricingDialog;